import { View } from 'react-native'
import React, { Children } from 'react'
import ChecklistStepComponent from './ChecklistStepComponent'
import { ButtonProps } from '@ui/buttons/Button/types'
import { ChecklistItemState } from './types'
import cn from '@ui/utils/cn'

export type ChecklistProps = {
  step?: number
  setStep?: (s: number) => void
  steps: ChecklistStep[]
  className?: string
}

export type ChildrenChecklistProps = Omit<ChecklistProps, 'steps'> & {
  children: React.ReactNode
}

export type ChecklistStep = {
  node?: React.ReactNode
  nodeChecked?: React.ReactNode
  title: string
  desc?: string
  action?: React.ReactNode
  button?: ButtonProps
  disabled?: boolean
  last?: boolean
  state?: ChecklistItemState
  staticBody?: boolean
  hideNumber?: boolean
  hideLine?: boolean
  showLineOpen?: boolean
  isExpandable?: boolean
  defaultOpen?: boolean
  hidden?: boolean
}

const getState = (step: number, index: number) => {
  if (step > index) return 'done'
  if (step === index) return 'todo'
  return 'upcoming'
}

export type ChecklistItemRenderProps = {
  step?: number
  index?: number
  setStep?: ChecklistProps['setStep']
  last?: boolean
  hidden?: boolean
}

type ChecklistStepItemProps = ChecklistStep & ChecklistItemRenderProps

const ChecklistStepItem = (props: ChecklistStepItemProps) => {
  const { step = 0, setStep, index = 0, ...s } = props
  const localState = getState(step, index) || 'upcoming'
  return (
    <ChecklistStepComponent
      key={index}
      index={index}
      setStep={setStep}
      step={step}
      disabled={step ? step < index : undefined}
      state={localState}
      {...s}
    />
  )
}

ChecklistStepItem.displayName = 'Checklist.Step'

const ChecklistContainer = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => (
  <View className={cn('flex flex-col items-start', className)}>{children}</View>
)

const Checklist = ({
  step,
  setStep,
  className,
  ...props
}: ChecklistProps | ChildrenChecklistProps) => {
  // Handle Checklist.Step children
  if ('children' in props) {
    const childrenArr = Children.toArray(props.children)
    const filtered = childrenArr
      .filter(React.isValidElement<ChecklistItemRenderProps>)
      .filter((el) => !el.props.hidden)

    const childrenSteps = filtered.map((child, index) => {
      return React.cloneElement(child, {
        index,
        setStep,
        step,
        last: index === filtered.length - 1,
      })
    })
    return (
      <ChecklistContainer className={className}>
        {childrenSteps}
      </ChecklistContainer>
    )
  }
  const { steps } = props
  return (
    <ChecklistContainer className={className}>
      {steps
        .filter((s) => !s.hidden)
        .map((s, index) => {
          const state: ChecklistItemState =
            s.state || (step !== undefined ? getState(step, index) : 'upcoming')

          return (
            <ChecklistStepComponent
              key={index}
              index={index}
              setStep={setStep}
              step={step}
              disabled={step ? step < index : undefined}
              last={index === steps.length - 1}
              {...s}
              state={state}
            />
          )
        })}
    </ChecklistContainer>
  )
}
Checklist.Step = React.memo(ChecklistStepItem)

export default Checklist
